import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'default' | 'ghost' | 'danger';
};

export function Button({ variant = 'default', className, ...props }: ButtonProps) {
  const base =
    'inline-flex h-11 items-center justify-center gap-2 rounded-[10px] px-4 text-sm font-semibold whitespace-nowrap transition disabled:opacity-45 disabled:cursor-default cursor-pointer';
  const variants = {
    primary: 'bg-accent text-[#04141d] hover:bg-[#5cd2ff] shadow-[0_6px_22px_rgba(62,200,255,.22)]',
    default: 'bg-panel2 border border-border text-txt hover:bg-hover',
    ghost: 'bg-transparent border border-border text-txt hover:bg-hover',
    danger: 'bg-transparent border border-[rgba(240,84,79,.3)] text-review hover:bg-[rgba(240,84,79,.08)]',
  } as const;
  return <button className={cn(base, variants[variant], className)} {...props} />;
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        'h-11 w-full rounded-[10px] border border-border bg-bg px-3 text-sm text-txt outline-none transition placeholder:text-faint focus:border-accentdeep',
        className,
      )}
      {...props}
    />
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] text-muted">{label}</span>
      {children}
      {hint && <span className="text-[11.5px] text-faint">{hint}</span>}
    </label>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-2xl border border-bordersoft bg-panel', className)}>{children}</div>
  );
}

export function PanelTitle({ icon, title, count }: { icon: ReactNode; title: string; count?: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-bordersoft px-[18px] py-[15px] text-[13.5px] font-semibold">
      <span className="text-accent">{icon}</span>
      {title}
      {count != null && (
        <span className="ml-auto font-mono text-[11px] tracking-wide text-faint">{count}</span>
      )}
    </div>
  );
}

export const actionColor: Record<string, string> = {
  allow: 'var(--color-allow)',
  verify: 'var(--color-verify)',
  review: 'var(--color-review)',
};

export function rgba(action: string, a: number): string {
  const m: Record<string, string> = {
    allow: '70,201,138',
    verify: '246,176,60',
    review: '240,84,79',
  };
  return `rgba(${m[action] ?? '136,143,158'},${a})`;
}

export function Badge({ action }: { action: string }) {
  return (
    <span
      className="inline-block rounded-[5px] px-[7px] py-0.5 font-mono text-[9.5px] uppercase tracking-[0.13em]"
      style={{ background: rgba(action, 0.14), color: actionColor[action] }}
    >
      {action}
    </span>
  );
}

export function Spinner() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-accent" />
    </div>
  );
}
